// password_strength.ts - Handle auto-check password strength validation

import { on } from 'delegated-events'

interface AutoCheckSuccessEvent extends Event {
  detail: {
    response: Response
  }
}

// Store initial HTML for each container to restore when password is cleared
const initialHtmlMap = new WeakMap<HTMLElement, string>()

// Handle auto-check success event
on('auto-check-success', '.js-password-strength', function (event: Event): void {
  const autoCheckEvent = event as AutoCheckSuccessEvent
  const input = event.currentTarget as HTMLInputElement
  const autoCheck = input.closest('auto-check')
  if (!autoCheck) return

  const state = autoCheck.querySelector<HTMLElement>('.js-password-strength-container')
  if (!state) return

  // Store initial HTML on first interaction
  if (!initialHtmlMap.has(state)) {
    initialHtmlMap.set(state, state.innerHTML)
  }

  autoCheckEvent.detail.response.text().then(function (data: string): void {
    if (data) {
      state.innerHTML = data
    }
  })
})

// Clear feedback when input is cleared
on('change', '.js-password-strength', function (event: Event): void {
  const input = event.currentTarget as HTMLInputElement
  const autoCheck = input.closest('auto-check')
  if (!autoCheck) return

  const state = autoCheck.querySelector<HTMLElement>('.js-password-strength-container')
  if (!state) return

  if (input.value.length === 0) {
    // Restore initial HTML instead of clearing
    const initialHtml = initialHtmlMap.get(state)
    if (initialHtml) {
      state.innerHTML = initialHtml
    }
  }
})

// Debounce helper function
function debounce<T extends (...args: Parameters<T>) => void>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | undefined

  return function (...args: Parameters<T>): void {
    const later = (): void => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// Check password strength as user types (debounced)
const checkPasswordStrength = debounce(function (
  input: HTMLInputElement,
  container: HTMLElement,
  url: string,
  csrf: string
): void {
  const password = input.value

  // Store initial HTML on first interaction
  if (!initialHtmlMap.has(container)) {
    initialHtmlMap.set(container, container.innerHTML)
  }

  if (!password || password.length === 0) {
    // Restore initial HTML instead of clearing
    const initialHtml = initialHtmlMap.get(container)
    if (initialHtml) {
      container.innerHTML = initialHtml
    }
    return
  }

  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRF-Token': csrf
    },
    body: `password=${encodeURIComponent(password)}`
  })
    .then((response: Response) => response.text())
    .then((html: string) => {
      container.innerHTML = html
    })
    .catch((error: Error) => {
      // eslint-disable-next-line no-console
      console.error('Password strength check failed:', error)
    })
}, 300)

// Listen for input events as user types
on('input', '.js-password-strength', function (event: Event): void {
  const input = event.currentTarget as HTMLInputElement
  const autoCheckElement = input.closest('auto-check')
  if (!autoCheckElement) return

  const container = autoCheckElement.querySelector<HTMLElement>('.js-password-strength-container')
  const url = autoCheckElement.getAttribute('src')
  const csrf = autoCheckElement.getAttribute('csrf')

  if (container && url && csrf) {
    checkPasswordStrength(input, container, url, csrf)
  }
})
